import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import {
    findDelimiterColonIndex,
    hasDelimiterColon,
} from 'components/WorkspaceEditor/features/drawingDesignation/services/textParser'
import { hasDesignationFormatInText } from 'components/WorkspaceEditor/features/drawingDesignation/utils/designationFormatValidator'
import type { DataNode } from 'components/WorkspaceEditor/types'
import { isManufacturerOrBODHeader } from 'components/WorkspaceEditor/utils'
import { TrackChangeType } from 'models/changes'
import { useCallback, useMemo, useRef, useState } from 'react'
import { getConcatenatedText } from '../utils/textUtils'

/**
 * Custom hook that handles parsing and manipulating keyed text content.
 *
 * @param {Partial<TextContentElement>} contentItem - The content item data, potentially incomplete.
 * @param {boolean} [isContentTeamFromStudioPage] - Whether the current content is from SMM/OM with special permissions.
 * @param {DataNode} [node] - The parent node, used to check for MFR/BOD exclusions.
 * @returns An object containing parsed text and handlers for managing keyed content.
 */

export const useKeyedText = (
    contentItem: Partial<TextContentElement>,
    isContentTeamFromStudioPage?: boolean,
    node?: DataNode,
    isAfterKeyDelimiter?: boolean
) => {
    const text = useMemo(() => getConcatenatedText(contentItem), [contentItem])
    const serverDesignation = node?.contentItemView?.drawingDesignation
    const textHasDelimiterColon =
        findDelimiterColonIndex(text, serverDesignation) !== -1

    // Check if this content has FieldRef elements (used for drawing designations)
    const hasFieldRefElement = useMemo(
        () =>
            contentItem.elements?.some((el) => el.type === 'FieldRef') ?? false,
        [contentItem.elements]
    )

    // Check if this is MFR/BOD base content (header node)
    // MFR/BOD headers contain colons but are NOT key-value pairs
    const isMfrBodBaseContent = node ? isManufacturerOrBODHeader(node) : false

    const isExcludedForOtherReasons = Boolean(
        contentItem.changeType?.toUpperCase() === TrackChangeType.ADDED || // user-added content
        isMfrBodBaseContent || // exclude MFR/BOD base content (Bug 2568131 fix)
        isAfterKeyDelimiter // elements after the key colon are value content, not keys
    )

    const isExcludedContent =
        isContentTeamFromStudioPage || isExcludedForOtherReasons // exclude studio page content

    const isArticle = node?.type === 'Article'

    const calculatedHasDesignationFormat =
        hasDesignationFormatInText(text) && !isExcludedContent
    const calculatedIsKeyedText = textHasDelimiterColon && !isExcludedContent

    // Once content is identified as keyed text, it should remain stable throughout the editing session
    const [isKeyedText] = useState(calculatedIsKeyedText)

    const [hasKeySplit] = useState(
        textHasDelimiterColon && !isExcludedForOtherReasons
    )

    // This ensures DesignationField keeps showing even if designation is cleared, and handles
    const hasDesignationFormatRef = useRef(calculatedHasDesignationFormat)
    if (calculatedHasDesignationFormat && !hasDesignationFormatRef.current) {
        hasDesignationFormatRef.current = true
    }
    const hasDesignationFormat = hasDesignationFormatRef.current

    // Capture the initial keyed text and preserve it throughout the session
    const [initialKeyedText] = useState(() => {
        if (!hasKeySplit) return ': '
        const colonIndex = findDelimiterColonIndex(text, serverDesignation)
        if (colonIndex === -1) return ': '

        return text.substring(0, colonIndex + 1)
    })

    // This ensures drawing designations update after mutation while maintaining stability during editing
    const stableKeyedText = useMemo(() => {
        if (!hasKeySplit) return ': '

        // If current text has a colon, extract the key from it (for dynamic updates like drawing designations)
        const colonIndex = findDelimiterColonIndex(text, serverDesignation)
        if (colonIndex !== -1) {
            return text.substring(0, colonIndex + 1)
        }

        // If current text doesn't have a colon, use the preserved initial key
        return initialKeyedText
    }, [text, hasKeySplit, initialKeyedText, serverDesignation])

    const { keyedText, keyedTextValue } = useMemo(() => {
        // Case 1: Traditional keyed text with colon (locked keys), or excluded content
        // (e.g. OM/content-team) that still has a real colon-delimited key.
        if (hasKeySplit) {
            const valuePart = text.includes(':')
                ? text.substring(stableKeyedText.length)
                : undefined
            return {
                keyedText: stableKeyedText,
                keyedTextValue: valuePart,
            }
        }

        // Case 2: Designation format without colon
        if (hasDesignationFormat) {
            return {
                keyedText: text + ':',
                keyedTextValue: undefined,
            }
        }

        // Case 3: Article without colon - use full text as keyedText (no colon suffix)
        if (isArticle && text.trim()) {
            return {
                keyedText: text,
                keyedTextValue: undefined,
            }
        }

        // Case 4: No keyed text and no designation format
        return { keyedText: ': ', keyedTextValue: undefined }
    }, [text, hasKeySplit, stableKeyedText, hasDesignationFormat, isArticle])

    /**
     * Track-changes split can leave the first element holding only a key
     * prefix; treat that as "key already present" so we don't prepend a
     * duplicate on blur save. Bug 2667445.
     */
    const keyElementExists = useCallback(
        (elements: Partial<TextContentElement>[]): boolean => {
            const trimmedKey = keyedText.trim()
            return elements.some((element) => {
                const elementText = element.text?.trim()
                if (!elementText) return false
                if (elementText.startsWith(trimmedKey)) return true
                // Element is a key fragment only while it hasn't crossed `):`.
                return (
                    trimmedKey.startsWith(elementText) &&
                    !hasDelimiterColon(elementText)
                )
            })
        },
        [keyedText]
    )

    /**
     * Generates unique ID for key element
     */
    const generateKeyElementId = useCallback(
        (elements: Partial<TextContentElement>[]): string => {
            const firstElement = elements[0]
            return firstElement?.id ? `${firstElement.id}_key` : 'generated_key'
        },
        []
    )

    /**
     * Prepends a new element containing only the key text (e.g., "Label: ") to the array,
     * resetting change tracking while preserving the original element's ID.
     */
    const prependKeyedTextElement = useCallback(
        (textElements: Partial<TextContentElement>[]) => {
            // hasKeySplit (not isKeyedText) so this also re-prepends the key for OM/
            // content-team excluded content that still has a real colon-delimited key.
            if (!hasKeySplit || textElements.length === 0) return

            // Skip prepending for FieldRef-based keyed text
            if (hasFieldRefElement) return

            // Prevent duplication - exit early if key element already exists
            if (keyElementExists(textElements)) return

            const keyPreservingElement: Partial<TextContentElement> = {
                id: generateKeyElementId(textElements),
                text: keyedText,
                changeType: null,
                authoredUserId: '',
            }

            // prepend the keyedText element at the start of textElements
            textElements.unshift(keyPreservingElement)
        },
        [
            hasKeySplit,
            keyedText,
            hasFieldRefElement,
            keyElementExists,
            generateKeyElementId,
        ]
    )

    return {
        keyedText,
        keyedTextValue,
        isKeyedText,
        hasDesignationFormat,
        prependKeyedTextElement,
    }
}
